import type { SignalNativeSeriesV1, SignalMinMaxSeriesV1 } from "@rekonas/domain";
export interface ChannelReadout { text: string; description: string }
const finite=(value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
function number(value: number, unit: string): string {
  // Metadata supplies units; no inferred percentages or clinical classification.
  return new Intl.NumberFormat(undefined,{maximumFractionDigits:["%","bpm"].includes(unit) ? 1 : 3}).format(value);
}
function atOrBefore<T>(items: readonly T[], seconds: number, start: (item: T)=>number): T | undefined {
  let low=0, high=items.length;
  while (low<high) { const mid=(low+high)>>>1; if (start(items[mid])<=seconds) low=mid+1; else high=mid; }
  return items[low-1];
}
/** Native: nearest sample's half-period support, never interpolation/forward fill.
 * Overview: report the containing aggregate interval explicitly, never a midpoint. */
export function channelValueAt(series: SignalNativeSeriesV1 | SignalMinMaxSeriesV1, seconds: number): ChannelReadout {
  const missing={text:"Not available",description:"No supported finite sample at this time"};
  if (!finite(seconds)) return missing;
  const suffix=series.unit === "%" ? "%" : series.unit ? ` ${series.unit}` : "";
  if (series.representation === "native") {
    const period=series.sample_period_seconds;
    if (!finite(period) || period<=0 || !finite(series.start_seconds)) return missing;
    const index=Math.floor((seconds-series.start_seconds)/period+0.5);
    if (index<0 || index>=Math.min(series.sample_count,series.values.length)) return missing;
    const value=series.values[index], time=series.start_seconds+index*period;
    if (!finite(value) || Math.abs(seconds-time)>period/2+1e-9) return missing;
    return {text:`${number(value,series.unit)}${suffix}`,description:`Native sample at ${time.toFixed(3)} s; nearest sample within half the ${period} s sample period. No interpolation.`};
  }
  const gap=atOrBefore(series.gaps || [],seconds,item=>item.start_seconds);
  if (gap && seconds<gap.end_seconds) return missing;
  const bin=atOrBefore(series.bins,seconds,item=>item.start_seconds);
  if (!bin || seconds>=bin.end_seconds || bin.valid_count<=0 || !finite(bin.minimum) || !finite(bin.maximum)) return missing;
  return {text:`Range ${number(bin.minimum,series.unit)}–${number(bin.maximum,series.unit)}${suffix}`,
    description:`Aggregate range for ${bin.start_seconds.toFixed(3)}–${bin.end_seconds.toFixed(3)} s; not an instantaneous sample.`};
}
